import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/styles';

import { Box, Typography, Grid, Button, TextField, InputAdornment, IconButton } from '@material-ui/core';
import AuthCard from 'components/Cards/AuthCard';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import palette from 'theme/palette';

const useStyles = makeStyles((theme) => ({
  instructions: {
    ...theme.typography.subtitle2,
  },
  textWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  title: {
    ...theme.typography.h5,
    marginBottom: theme.spacing(2),
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
  },
  errorMessage: {
    color: palette.error.main,
  },
}));

const PasswordResetForm = ({ onSubmit, expiration }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [password, setPassword] = useState('');
  const [passwordconfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const validatePassword = (name, value) => {
    if (name === 'password') {
      if (!value || value.length === 0) {
        return { invalid: true, message: t('field_required') };
      }
    } else if (name === 'passwordconfirm') {
      if (!value || value.length === 0) {
        return { invalid: true, message: t('field_required') };
      }
      if (value !== password) {
        return { invalid: true, message: t('passwords_not_match') };
      }
    }
    return { invalid: false, message: '' };
  };

  const validForm = () => {
    let checkValidPassword = validatePassword('password', password);

    if (checkValidPassword.invalid) {
      return checkValidPassword.message;
    }
    checkValidPassword = validatePassword('passwordconfirm', passwordconfirm);
    if (checkValidPassword.invalid) {
      return checkValidPassword.message;
    }
    return true;
  };

  return (
    <AuthCard>
      <Grid container direction="row" justifyContent="space-evenly" spacing={4}>
        <Grid container item direction="row" justifyContent="space-evenly" spacing={1}>
          <Grid item xs={12}>
            <Box className={classes.textWrapper}>
              <Typography className={classes.title}>{t('reset_password')}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.textWrapper}>
              <Typography className={classes.instructions}>{t('reset_instructions_enter_new_password')}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.root}>
              <Typography className={classes.instructions}>
                {t('page_expiration')}
                {expiration}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.root}>
              <Typography className={classes.errorMessage}>{validForm()}</Typography>
            </Box>
          </Grid>
        </Grid>

        <Box component="div" className={classes.root}>
          <Grid container item xs={12} direction="row" justifyContent="center" spacing={4}>
            <Grid item xs={12}>
              <TextField
                required
                error={validatePassword('password', password).invalid}
                name="password"
                value={password}
                type={showPassword ? 'text' : 'password'}
                label={t('password')}
                onChange={(v) => {
                  setPassword(v.target.value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                error={validatePassword('passwordconfirm', passwordconfirm).invalid}
                name="passwordconfirm"
                value={passwordconfirm}
                type={showPassword ? 'text' : 'password'}
                label={t('confirm_password')}
                onChange={(v) => {
                  setPasswordConfirm(v.target.value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={!validForm()}
            className={classes.submit}
            onClick={() => onSubmit(password)}
          >
            {t('reset_password')}
          </Button>
        </Grid>
      </Grid>
    </AuthCard>
  );
};

PasswordResetForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  expiration: PropTypes.string.isRequired,
};

PasswordResetForm.defaultProps = {};

export default PasswordResetForm;
